import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { getRFQ, deleteRFQ, getQuotationDetails, getRFQQuotes, sendRFQ, resendRFQ } from '../../../models/rfqModel';
import api from '../../../services/api';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { safeParseDate } from '../../../utils/dateUtils';
import QuoteModal from '../../../components/modals/QuoteModal';
import { colors } from '../../../utils/colors';
import procurementService from '../../../services/procurementService';
import { getSuppliers as fetchAllSuppliers, getSupplierNames, updateSpecificationSuppliers } from '../../../models/specificationModel';
import '../../../styles/colors.css';

// Safe JSON parsing utility
const safeJsonParse = (jsonString, defaultValue = []) => {
  try {
    if (!jsonString || typeof jsonString !== 'string') {
      return defaultValue;
    }
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Failed to parse JSON:', error, 'Input:', jsonString);
    return defaultValue;
  }
};

// PHP unserialize helper for specification data
const parsePhpSerialized = (serializedString, defaultValue = []) => {
  if (!serializedString) return defaultValue;
  
  try {
    // Parse PHP serialized array format: a:2:{i:0;s:73:"...";i:1;s:106:"...";} or a:19:{i:0;i:11111111;...}
    const arrayMatch = serializedString.match(/^a:(\d+):\{(.+)\}$/s);
    if (arrayMatch) {
      const items = [];
      const content = arrayMatch[2];
      let pos = 0;
      
      while (pos < content.length) {
        // Skip whitespace
        while (pos < content.length && /\s/.test(content[pos])) pos++;
        
        // Look for index marker: i:N;
        const indexMatch = content.substring(pos).match(/^i:\d+;/);
        if (!indexMatch) break;
        pos += indexMatch[0].length;
        
        // Skip whitespace
        while (pos < content.length && /\s/.test(content[pos])) pos++;
        
        // Look for value type
        if (content.substring(pos).startsWith('s:')) {
          // String value: s:BYTE_LENGTH:"VALUE";
          // PHP serialize() stores byte length (UTF-8), but JS substring() counts
          // characters (UTF-16). Multi-byte chars (e.g. ™ = 3 bytes, 1 char) make
          // the byte count larger than the JS character count, causing overshoot.
          // Fix: advance character-by-character until we have consumed exactly
          // byteLength UTF-8 bytes, then verify the closing delimiter.
          const lengthMatch = content.substring(pos).match(/^s:(\d+):/);
          if (!lengthMatch) break;
          
          const byteLength = parseInt(lengthMatch[1], 10);
          pos += lengthMatch[0].length; // Move past "s:BYTE_LENGTH:"
          
          // Expect opening quote
          if (content[pos] !== '"') break;
          pos++; // Skip opening quote
          
          // Consume exactly byteLength UTF-8 bytes by walking characters
          let bytesConsumed = 0;
          let strStart = pos;
          while (pos < content.length && bytesConsumed < byteLength) {
            const code = content.codePointAt(pos);
            // Determine UTF-8 byte width of this code point
            if (code < 0x80) {
              bytesConsumed += 1;
              pos += 1;
            } else if (code < 0x800) {
              bytesConsumed += 2;
              pos += 1;
            } else if (code < 0x10000) {
              bytesConsumed += 3;
              pos += 1;
            } else {
              // Surrogate pair (code > 0xFFFF): 4 UTF-8 bytes, 2 JS chars
              bytesConsumed += 4;
              pos += 2;
            }
          }
          const stringValue = content.substring(strStart, pos);
          
          // Expect closing quote
          if (content[pos] !== '"') break;
          pos++; // Skip closing quote
          
          // Expect semicolon
          if (content[pos] !== ';') break;
          pos++; // Skip semicolon
          
          items.push(stringValue);
          
        } else if (content.substring(pos).startsWith('i:')) {
          // Integer value: i:VALUE;
          const intMatch = content.substring(pos).match(/^i:(\d+);/);
          if (!intMatch) break;
          
          items.push(intMatch[1]); // Store as string for display
          pos += intMatch[0].length;
        }
      }
      
      return items.length > 0 ? items : defaultValue;
    }
    
    // Fallback to JSON parsing
    return JSON.parse(serializedString);
  } catch (error) {
    console.warn('Failed to parse PHP serialized data:', error, 'Input:', serializedString);
    return defaultValue;
  }
};

// Helper function to get suppliers with their last sent information
const getSuppliers = (rfq) => {
  if (!rfq) return [];
  
  // Use suppliers array as the base, then enrich with sent information
  if (rfq.suppliers && rfq.suppliers.length > 0) {
    return rfq.suppliers.map(supplier => {
      // Find all sent entries for this supplier
      const supplierSentEntries = rfq.sent ? rfq.sent.filter(sent => sent.BP_Code === supplier.BP_Code) : [];
      
      // Get the last sent entry (highest timestamp)
      const lastSentEntry = supplierSentEntries.length > 0 
        ? supplierSentEntries.reduce((latest, current) => 
            (parseInt(current.rfq_sent) > parseInt(latest.rfq_sent)) ? current : latest
          )
        : null;
      
      return {
        ...supplier,
        // Add sent-related fields
        rfq_id: lastSentEntry?.rfq_id || null,
        rfq_sent: lastSentEntry?.rfq_sent || null,
        send_count: supplierSentEntries.length
      };
    });
  }
  
  // Fallback to sent array for backwards compatibility (but this shouldn't be the primary case)
  if (rfq.sent && rfq.sent.length > 0) {
    return rfq.sent;
  }
  
  return [];
};

function RFQView() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [otpValues, setOtpValues] = useState({});
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [extendData, setExtendData] = useState({
    newDate: '',
    newTime: '',
    notifySuppliers: true
  });

  // Supplier editing states
  const [showEditSuppliersModal, setShowEditSuppliersModal] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierNames, setSupplierNames] = useState({});
  const [isUpdatingSuppliers, setIsUpdatingSuppliers] = useState(false);

  useEffect(() => {
    fetchRFQ();
  }, [rfqId]);

  
  const fetchRFQ = async () => {
    try {
      setLoading(true);
      const response = await getRFQ(rfqId);
      
      if (response.status === 1) {
        setRfq(response.data);
        
        // Try to get actual submissions using the submissions endpoint
        try {
          const quotesResponse = await getRFQQuotes(rfqId); 
          
          if (quotesResponse.status === 1 && quotesResponse.data && quotesResponse.data.length > 0) {
            // Use only the actual submissions from the API
            setSubmissions(quotesResponse.data); 
          } else {
            // No actual submissions found - set empty array
            setSubmissions([]); 
          }
        } catch (quotesError) {
          console.warn('Failed to fetch quotes:', quotesError);
          // No fallback - just empty submissions if API fails
          setSubmissions([]);
        }
        
      } else {
        toast.error(response.message || 'Failed to load RFQ');
        navigate('/dashboard/rfqs');
      }
    } catch (error) {
      console.error('Error fetching RFQ:', error);
      toast.error('Failed to load RFQ');
      navigate('/dashboard/rfqs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${rfq?.procurement_title}"?`)) {
      try {
        const response = await deleteRFQ(rfqId);
        if (response.status === 1) {
          toast.success('RFQ deleted successfully');
          navigate('/dashboard/rfqs');
        } else {
          toast.error(response.message || 'Failed to delete RFQ');
        }
      } catch (error) {
        toast.error('Failed to delete RFQ');
      }
    }
  };

  const handlePermanentDelete = async () => {
    // Check if RFQ is published
    const isPublished = rfq.procurement_status === 1;
    
    // Show warning based on status
    const result = await Swal.fire({
      title: 'Permanently Delete Specification?',
      html: isPublished 
        ? `
          <div class="text-left">
            <p class="mb-3 text-red-600 font-semibold">⚠️ This RFQ has been sent to suppliers.</p>
            <p class="mb-2">Permanently deleting will:</p>
            <ul class="list-disc list-inside mb-3 text-sm text-gray-700">
              <li>Remove the specification from the system</li>
              <li>NOT cancel the RFQ sent to suppliers</li>
              <li>Suppliers can still submit quotations until closing date</li>
            </ul>
            <p class="text-sm font-bold text-red-600">This action cannot be undone!</p>
          </div>
        `
        : `
          <div class="text-left">
            <p class="mb-2">This will permanently delete the specification from the system.</p>
            <p class="text-sm font-bold text-red-600">This action cannot be undone!</p>
          </div>
        `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, permanently delete',
      cancelButtonText: 'Cancel'
    });
    
    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await api.put(`/api/specifications/${rfq.specification_id}/permanent-delete`);
      if (response.data && response.data.status === 1) {
        await Swal.fire({
          title: 'Deleted!',
          text: 'Specification has been permanently deleted.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
        navigate('/dashboard/rfqs');
      } else {
        await Swal.fire({
          title: 'Error!',
          text: response.data?.message || 'Failed to permanently delete specification.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error permanently deleting specification:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to permanently delete specification',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleUnpublish = async () => {
    const result = await Swal.fire({
      title: 'Unpublish RFQ?',
      html: `
        <div class="text-left">
          <p class="mb-3">This will revert the RFQ to draft status.</p>
          <p class="mb-2">The RFQ will:</p>
          <ul class="list-disc list-inside mb-3 text-sm text-gray-700">
            <li>Be removed from the published list</li>
            <li>No longer be accessible to suppliers</li>
            <li>Return to draft mode for editing</li>
          </ul>
          <p class="text-sm font-bold text-blue-600">You can re-publish it later after making changes.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0066cc',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, unpublish',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await api.put(`/api/procurement/${rfq.procurement_id}/unpublish`);
      if (response.data && response.data.status === 1) {
        await Swal.fire({
          title: 'Unpublished!',
          text: 'RFQ has been unpublished and returned to draft status.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
        fetchRFQ(); // Refresh to show updated status
      } else {
        await Swal.fire({
          title: 'Error!',
          text: response.data?.message || 'Failed to unpublish RFQ.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error unpublishing RFQ:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to unpublish RFQ',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleSend = async () => {
    if (window.confirm(`Are you sure you want to send "${rfq?.procurement_title}" to suppliers?`)) {
      try {
        setIsSending(true);
        const response = await sendRFQ(rfqId);
        if (response.status === 1) {
          toast.success('RFQ sent to suppliers successfully');
          fetchRFQ(); // Refresh the data to show updated status
        } else {
          toast.error(response.message || 'Failed to send RFQ');
        }
      } catch (error) {
        toast.error('Failed to send RFQ');
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleResend = async (supplier) => {
    if (window.confirm(`Are you sure you want to resend "${rfq?.procurement_title}" to ${supplier?.BP_Name}?`)) {
      try {
        const response = await sendRFQ(rfqId, supplier.BP_Code);
        if (response.status === 1) {
          toast.success(`RFQ resent to ${supplier.BP_Name} successfully`);
          fetchRFQ();
        } else {
          toast.error(response.message || 'Failed to resend RFQ');
        }
      } catch (error) {
        toast.error('Failed to resend RFQ');
      }
    }
  };

  const handleExtend = () => {
    // Initialize extend data with current values
    setExtendData({
      newDate: rfq?.procurement_closing || '',
      newTime: rfq?.procurement_closing_time || '',
      notifySuppliers: true
    });
    setShowExtendModal(true);
  };

  const handleExtendSubmit = async () => {
    try {
      // Create the API payload matching backend expectations
      const payload = {
        procurement_id: rfqId,
        current_deadline: `${rfq.procurement_closing} ${rfq.procurement_closing_time}`,
        new_deadline: `${extendData.newDate} ${extendData.newTime}`,
        procurement_extend_date: extendData.newDate,
        procurement_extend_time: extendData.newTime,
        procurement_extend_notify: extendData.notifySuppliers ? 1 : 0,
        extension_reason: 'Extended via React frontend'
      };

      const response = await api.post(`/api/procurement/extend/${rfqId}`, payload);
      
      if (response.data.status === 1) {
        toast.success('RFQ closing date extended successfully');
        setShowExtendModal(false);
        fetchRFQ(); // Refresh the data
      } else {
        toast.error(response.data.message || 'Failed to extend RFQ closing date');
      }
    } catch (error) {
      console.error('Error extending RFQ:', error);
      toast.error('Failed to extend RFQ closing date');
    }
  };

  // Supplier editing functions
  const handleOpenEditSuppliersModal = async () => {
    try {
      // Fetch all available suppliers
      const suppliers = await fetchAllSuppliers();
      setAllSuppliers(suppliers);

      // Get currently selected suppliers from RFQ
      const currentSuppliers = getSuppliers(rfq);
      const currentSupplierCodes = currentSuppliers.map(s => ({
        BP_Code: s.BP_Code,
        BP_Name: s.BP_Name
      }));
      setSelectedSuppliers(currentSupplierCodes);

      // Fetch supplier names for selected suppliers
      if (currentSupplierCodes.length > 0) {
        const codes = currentSupplierCodes.map(s => s.BP_Code);
        const names = await getSupplierNames(codes);
        setSupplierNames(names);
      }

      setShowEditSuppliersModal(true);
    } catch (error) {
      console.error('Error opening supplier edit modal:', error);
      toast.error('Failed to load suppliers');
    }
  };

  const handleUpdateSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier');
      return;
    }

    try {
      setIsUpdatingSuppliers(true);

      // Extract supplier codes
      const supplierCodes = selectedSuppliers.map(s => 
        typeof s === 'object' ? s.BP_Code : s
      );

      // Use dedicated endpoint to update only suppliers (prevents data loss)
      const response = await updateSpecificationSuppliers(rfq.specification_id, supplierCodes);

      if (response.status === 1) {
        toast.success('Suppliers updated successfully');
        setShowEditSuppliersModal(false);
        fetchRFQ(); // Refresh to show updated suppliers
      } else {
        toast.error(response.message || 'Failed to update suppliers');
      }
    } catch (error) {
      console.error('Error updating suppliers:', error);
      toast.error('Failed to update suppliers');
    } finally {
      setIsUpdatingSuppliers(false);
    }
  };

  // Handle marking RFQ as ready for sending
  const handleMarkReadyForSending = async () => {
    // Show SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: 'Mark as Ready for Sending?',
      text: 'This will allow buyers to send the RFQ to suppliers. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, mark as ready',
      cancelButtonText: 'Cancel'
    });
    
    if (!result.isConfirmed) {
      return; // User cancelled
    }

    try {
      setLoading(true);
      
      const response = await api.put(`/api/specifications/${rfq.specification_id}/mark-ready-for-sending`);
      
      if (response.data && response.data.status === 1) {
        await Swal.fire({
          title: 'Success!',
          text: 'RFQ has been marked as ready for sending.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
        // Refresh the RFQ data to update the status
        await fetchRFQ();
      } else {
        await Swal.fire({
          title: 'Error!',
          text: 'Failed to mark RFQ as ready for sending.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error marking RFQ as ready for sending:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to mark RFQ as ready for sending',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (rfq) => {
    if (!rfq) return { text: 'Unknown', class: 'bg-gray-100 text-gray-800' };
    
    if (rfq.procurement_closed) {
      return { text: 'Closed', class: 'bg-red-100 text-red-800' };
    }

    // If RFQ is expired (closing date reached), it's automatically unlocked
    if (isRFQExpired(rfq)) {
      return { text: 'Unlocked', class: 'px-2 py-1 text-xs font-medium rounded-full border text-white', style: { backgroundColor: 'var(--color-info)', borderColor: 'var(--color-info)' } };
    }
    
    switch (rfq.procurement_status) {
      case 0:
        return { text: 'Draft', class: 'bg-gray-100 text-gray-800' };
      case 1:
        return { text: 'Active', class: 'bg-green-100 text-green-800' };
      case 2:
        return { text: 'Waiting for OTP', class: 'px-2 py-1 text-xs font-medium rounded-full border text-white', style: { backgroundColor: 'var(--color-warning)', borderColor: 'var(--color-warning)' } };
      case 3:
        return { text: 'Unlocked', class: 'px-2 py-1 text-xs font-medium rounded-full border text-white', style: { backgroundColor: 'var(--color-info)', borderColor: 'var(--color-info)' } };
      default:
        return { text: 'Unknown', class: 'bg-gray-100 text-gray-800' };
    }
  };

  const isRFQExpired = (rfq) => {
    if (!rfq) return false;
    
    // Backend stores DD/MM/YYYY format, parse it correctly
    const closingDateStr = rfq.procurement_closing;
    if (!closingDateStr) return false;
    
    // Parse DD/MM/YYYY format correctly
    const match = closingDateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return false;
    
    const [, day, month, year] = match;
    const closingDate = new Date(year, month - 1, day); // Month is 0-indexed in JS
    
    // Create a combined date-time like PHP does: strtotime($date . ' ' . $time)
    const closingTime = rfq.procurement_closing_time || '23:59:59';
    const [timeHours, timeMinutes, timeSeconds] = closingTime.split(':').map(num => parseInt(num) || 0);
    
    closingDate.setHours(timeHours, timeMinutes, timeSeconds, 0);
    
    return closingDate < new Date();
  };

  const isSubmissionsUnlocked = (rfq) => {
    if (!rfq) return false;
    
    const supplierCount = getSuppliers(rfq).length;
    
    // Submissions are unlocked if:
    // 1. RFQ status is 3 (manually unlocked) OR
    // 2. RFQ has expired (closing date reached) OR
    // 3. Only a single supplier was selected (no competition, so viewing is allowed immediately)
    return rfq.procurement_status === 3 || 
           isRFQExpired(rfq) || 
           supplierCount === 1;
  };

  const shouldShowUnlockButton = (rfq) => {
    if (!rfq) return false;
    const supplierCount = getSuppliers(rfq).length;
    const submissionCount = submissions.length;
    
    return (
      !isRFQExpired(rfq) && // Not expired yet
      rfq.procurement_status === 1 && // Active/sent
      supplierCount > 1 && // Multiple suppliers
      submissionCount > 1 && // Has some submissions
      supplierCount > submissionCount // Not all suppliers submitted
    );
  };

  const shouldShowSendButton = (rfq) => {
    if (!rfq) return false;
    const suppliers = getSuppliers(rfq);
    const userRole = user?.role || user?.role_id;
    const isBuyer = userRole == 6 || userRole == 4;
    
    return (
      suppliers.length >= 1 && // At least one supplier selected
      (rfq.procurement_state === 0 || rfq.procurement_status === 0) && // RFQ is in draft state
      rfq.procurement_send === 1 && // RFQ is marked as ready to send  
      rfq.procurement_closed === 0 && // RFQ is not closed
      isBuyer && // User is a buyer (role 6)
      (rfq.specification_emergency === 0 || !rfq.specification_emergency) // Not an emergency specification
    );
  };

  const shouldShowExtendButton = (rfq) => {
    if (!rfq) return false;
    const suppliers = getSuppliers(rfq);
    const userRole = user?.role || user?.role_id;
    const isBuyer = userRole == 6 || userRole == 4;
    
    return (
      suppliers.length >= 1 && // At least one supplier
      rfq.procurement_state === 1 && // RFQ sent
      rfq.procurement_send === 1 && // Ready to send
      rfq.procurement_closed === 0 && // Not closed
      isBuyer && // User is a buyer (role 6)
      (rfq.specification_emergency === 0 || !rfq.specification_emergency) // Not emergency
    );
  };

  const handleUnlock = async () => {
    if (!window.confirm('Are you sure you want to unlock this RFQ? This will send OTP codes to approvers for confirmation.')) {
      return;
    }
    
    try {
      // Call the unlock API endpoint
      const response = await procurementService.unlockRFQ(rfq.procurement_id);
      if (response.status === 1) {
        toast.success('Unlock initiated. OTP codes have been sent to approvers.');
        // Show message that OTPs have been sent
        toast.info('Approvers will need to enter OTP codes to complete the unlock process.');
        fetchRFQ(); // Refresh data to show status change
      } else {
        toast.error(response.message || 'Failed to unlock RFQ');
      }
    } catch (error) {
      console.error('Error unlocking RFQ:', error);
      toast.error('Failed to unlock RFQ');
    }
  };

  const handleShowOTPModal = async () => {
    try {
      // Get approvers list
      const response = await procurementService.getApprovers(rfq.procurement_id);
      if (response.status === 1) {
        setApprovers(response.data || []);
        setOtpValues({});
        setShowOTPModal(true);
      } else {
        toast.error('Failed to load approvers');
      }
    } catch (error) {
      console.error('Error loading approvers:', error);
      toast.error('Failed to load approvers');
    }
  };

  const handleOTPSubmit = async () => {
    try {
      // Submit OTPs
      const response = await procurementService.confirmUnlockRFQ(rfq.procurement_id, otpValues);
      if (response.status === 1) {
        toast.success('RFQ unlocked successfully!');
        setShowOTPModal(false);
        fetchRFQ(); // Refresh data
      } else {
        toast.error(response.message || 'Invalid OTP codes. Please try again.');
      }
    } catch (error) {
      console.error('Error confirming unlock:', error);
      toast.error('Failed to confirm unlock');
    }
  };

  const handleOTPChange = (key, value) => {
    setOtpValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResendOTP = async (approver, index) => {
    try {
      await procurementService.resendOTP(approver.user_id, rfq.procurement_id, index);
      toast.success(`OTP resent to ${approver.user_full_name}`);
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP');
    }
  };

  const getRemainingTime = (rfq) => {
    if (!rfq) return 'Unknown';
    
    // Backend stores DD/MM/YYYY format, parse it correctly
    const closingDateStr = rfq.procurement_closing;
    if (!closingDateStr) return 'Unknown';
    
    // Parse DD/MM/YYYY format correctly
    const match = closingDateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return 'Unknown';
    
    const [, day, month, year] = match;
    const closingDate = new Date(year, month - 1, day); // Month is 0-indexed in JS
    
    // Create full closing datetime like PHP does
    const closingTime = rfq.procurement_closing_time || '23:59:59';
    const [timeHours, timeMinutes, timeSeconds] = closingTime.split(':').map(num => parseInt(num) || 0);
    
    closingDate.setHours(timeHours, timeMinutes, timeSeconds, 0);
    
    const now = new Date();
    
    if (closingDate < now) {
      return `Closed at ${rfq.procurement_closing_time} on ${rfq.procurement_closing}`;
    }
    
    const diff = closingDate.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${days} days, ${hours} hours, ${minutes} minutes`;
  };

  const handleViewQuote = async (submission) => {
    try {
      // Only handle real submission objects from the API
      if (typeof submission === 'object' && submission.quotation_id) { 
        setSelectedQuote(submission);
        setShowQuoteModal(true);
      } else {
        toast.error('Invalid submission data');
      }
    } catch (error) {
      console.error('Error handling quote view:', error);
      toast.error('Failed to display quote');
    }
  };

  const handleOpenSupplierLink = async (supplier) => {
    try {
      
      // Use first record for debugging if supplier is undefined
      let debugSupplier = supplier;
      const availableSuppliers = getSuppliers(rfq);
      if (!supplier && availableSuppliers.length > 0) {
        debugSupplier = availableSuppliers[0]; 
      }
      
      // Validate required data
      if (!rfq || !rfq.procurement_ref) {
        console.error('RFQ or procurement_ref is missing');
        toast.error('RFQ data is not available');
        return;
      }
      
      if (!debugSupplier || !debugSupplier.BP_Code || !debugSupplier.BP_Name) {
        console.error('Supplier data is incomplete:', debugSupplier);
        toast.error('Supplier information is incomplete');
        return;
      } 
      
      // Use the procurement service to generate a secure supplier link
      // This will call the backend API to properly encrypt the parameters
      const linkData = await procurementService.generateSupplierLink(
        rfq.procurement_ref,
        debugSupplier.BP_Code,
        debugSupplier.BP_Name
      ); 
      // Handle the API response structure - the actual data is in linkData.data
      const responseData = linkData.data || linkData; 
      
      // Always use the local origin instead of the backend-generated URL
      // The backend might be running on a different host (silulumanzi.host) 
      // but we want to use the current window location for the frontend
      const localBaseUrl = window.location.origin;
      const supplierUrl = `${localBaseUrl}/supplier/rfq/${responseData.encryptedRef}?SID=${responseData.encryptedSID}&S=${responseData.encryptedSupplierName}`;
       
      // Open in new tab
      window.open(supplierUrl, '_blank');
      
    } catch (error) { 
      
      // Use first record for debugging if supplier is undefined
      let fallbackSupplier = supplier;
      const availableSuppliers = getSuppliers(rfq);
      if (!supplier && availableSuppliers.length > 0) {
        fallbackSupplier = availableSuppliers[0]; 
      }
      
      // Validate data before fallback
      if (!rfq?.procurement_ref || !fallbackSupplier?.BP_Code || !fallbackSupplier?.BP_Name) {
 
        toast.error('Cannot generate supplier link - missing required information');
        return;
      }
       
      
      const baseUrl = window.location.origin;
      const encryptedRef = btoa(rfq.procurement_ref);
      const encryptedSID = btoa(fallbackSupplier.BP_Code.toString());
      const encryptedSupplierName = btoa(fallbackSupplier.BP_Name); 
      const supplierUrl = `${baseUrl}/supplier/rfq/${encryptedRef}?SID=${encryptedSID}&S=${encryptedSupplierName}`;
       
      window.open(supplierUrl, '_blank');
      
      // Show a warning that this is using fallback encryption
      toast.warning('Opened supplier link with temporary encryption. Please implement proper encryption API endpoint.');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R0.00';
    return `R${parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-4">RFQ Not Found</h3>
              <Link
                to="/dashboard/rfqs"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm transition-opacity"
                style={{ backgroundColor: 'rgb(0, 135, 212)' }}
              >
                Back to RFQs
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(rfq);
  const isExpired = isRFQExpired(rfq);
  const remainingTime = getRemainingTime(rfq);

  return (
    <>
      <style>
        {`
          .modal-extend-dialog .modal-content {
            overflow: visible !important;
          }
          .modal-extend-dialog {
            overflow: visible !important;
          }
        `}
      </style>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header Section */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex justify-between items-start gap-4">
            {/* Left: Logo, Title and Details */}
            <div className="flex items-start flex-1 min-w-0">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#22C55E" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-bold text-gray-800 truncate" title={rfq.procurement_title}>
                  {rfq.procurement_title}
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Request for Quotation
                </p>
                {/* Deletion Reason - displayed when marked for deletion */}
                {rfq.specification_status == 4 && rfq.specification_comment && (
                  <div className="mb-2 mt-1 p-2 bg-red-50 border-l-2 border-red-500 rounded">
                    <p className="text-xs font-semibold text-red-700 mb-0.5">Deletion Reason:</p>
                    <p className="text-xs text-red-600 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {rfq.specification_comment}
                    </p>
                  </div>
                )}
                {/* Recommended Action - displayed when RFQ is published and marked for deletion */}
                {rfq.specification_status == 4 && rfq.procurement_status === 1 && (() => {
                  const userRole = user?.role || user?.role_id;
                  return userRole == 6;
                })() && (
                  <div className="mb-2 mt-1 p-2 bg-blue-50 border-l-2 border-blue-500 rounded">
                    <p className="text-xs font-semibold text-blue-700 mb-0.5">💡 Recommended Action:</p>
                    <p className="text-xs text-blue-600">
                      Consider unpublishing this RFQ first before permanently deleting. This will remove it from suppliers' view and return it to draft mode.
                    </p>
                  </div>
                )}
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium truncate" style={{ color: '#22C55E' }} title={rfq.procurement_ref}>
                    {rfq.procurement_ref}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Two-Row Layout */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {/* First Row: Action buttons + Back button last */}
              <div className="flex items-center gap-2">
                {/* Action buttons */}
                {(() => {
                  const isDraft = rfq.procurement_status === 0;
                  const notMarkedForDeletion = rfq.specification_status != 4;
                  
                  // Get user data from flat user object structure
                  const userId = user?.user_id || user?.id;
                  const userDept = user?.department || user?.department_id;
                  const userRole = user?.role || user?.role_id;
                  
                  // Check access: creator, same department, or buyer (role 6)
                  const isCreator = rfq.specification_user == userId;
                  const sameDepartment = rfq.department == userDept;
                  const isBuyer = userRole == 6 || userRole == 4;
                  const hasAccess = isCreator || sameDepartment || isBuyer;
                  
                  // Calculate visibility for both Edit and Mark Ready buttons
                  const canEditOrMark = isDraft && notMarkedForDeletion && hasAccess;
                  
                  return (
                    <>
                      {canEditOrMark && (
                        <Link
                          to={`/dashboard/rfqs/${rfq.procurement_id}/edit`}
                          className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: 'var(--color-primary)' }}
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                          Edit
                        </Link>
                      )}

                      {/* Mark Ready for Sending Button - Same visibility as Edit button */}
                      {canEditOrMark && rfq.procurement_send !== 1 && (
                        <button
                          onClick={handleMarkReadyForSending}
                          className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: '#f97316' }}
                          title="Mark this RFQ as ready to be sent to suppliers"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Mark Ready for Sending
                        </button>
                      )}
                    </>
                  );
                })()}

                {/* Unpublish Button - Only shown when RFQ is published and marked for deletion (recommended action) */}
                {(() => {
                  const userRole = user?.role || user?.role_id;
                  return userRole == 6;
                })() && rfq.specification_status == 4 && rfq.procurement_status === 1 && (
                  <button
                    onClick={handleUnpublish}
                    className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#0066cc' }}
                    title="Unpublish RFQ and return to draft status"
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    Unpublish
                  </button>
                )}

                {/* Permanent Delete Button - Only for buyers when marked for deletion */}
                {(() => {
                  const userRole = user?.role || user?.role_id;
                  return userRole == 6;
                })() && rfq.specification_status == 4 && (
                  <button
                    onClick={handlePermanentDelete}
                    className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#f97316' }}
                    title="Permanently delete this specification"
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </button>
                )}
              
                {can("Delete RFQ") && rfq.procurement_status === 0 && rfq.specification_status != 4 && (
                  <button
                    onClick={handleDelete}
                    className="compact-button inline-flex items-center px-2 py-1 border rounded-md shadow-sm text-xs bg-white hover:bg-red-50 transition-colors"
                    style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </button>
                )}

                {/* Send Button - Hidden when marked for deletion */}
                {shouldShowSendButton(rfq) && rfq.specification_status != 4 && (
                  <button
                    onClick={handleSend}
                    disabled={isSending}
                    className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--color-success)' }}
                    title="Send RFQ to selected suppliers"
                  >
                    {isSending ? (
                      <>
                        <svg className="animate-spin w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Send RFQ
                      </>
                    )}
                  </button>
                )}

 
                  <button
                    onClick={handleExtend}
                    className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#f97316' }}
                    title="Extend closing date"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Extend Closing Date
                  </button>
         
                {/* Unlock Button - Hidden when marked for deletion */}
              {shouldShowUnlockButton(rfq) && rfq.specification_status != 4 && (
                <button
                  onClick={handleUnlock}
                  className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'var(--color-success)' }}
                  title="Unlock quotations for viewing before closing date"
                >
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 616 0z" clipRule="evenodd" />
                  </svg>
                  Unlock
                </button>
                )}

      

                {/* Edit Suppliers Button - visible for buyers (role 6) - Hidden when marked for deletion */}
                {(() => {
                  const isBuyer = user?.role == 6 || user?.role == 4;
                  const isMarkedForDeletion = rfq.specification_status == 4;
                  console.log('🔍 RFQView - Edit Suppliers button check:', { 
                    user: user, 
                    role: user?.role, 
                    role_id: user?.role_id,
                    role_name: user?.role_name,
                    isBuyer,
                    isMarkedForDeletion,
                    checkingForRole6: true
                  });
                  return isBuyer && !isMarkedForDeletion;
                })() && (
                  <button
                    onClick={handleOpenEditSuppliersModal}
                    className="compact-button inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#f97316' }}
                    title="Edit selected suppliers"
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    Edit Suppliers
                  </button>
                )}

                {/* Back button - Last */}
                <Link
                  to="/dashboard/rfqs"
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex-shrink-0"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to RFQs
                </Link>
              </div>

              {/* Second Row: Status badges and messages */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Status badges */}
                <span 
                  className={`px-2 py-1 text-xs font-medium rounded-full border ${statusInfo.class}`}
                  style={statusInfo.style}
                >
                  {statusInfo.text}
                </span>
                
                {/* Marked for Deletion Badge */}
                {rfq.specification_status == 4 && (
                  <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full border border-red-300">
                    <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Marked for Deletion
                  </span>
                )}
                
                {isExpired && (
                  <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-full border border-red-200">
                    <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 616 0z" clipRule="evenodd" />
                    </svg>
                    Expired
                  </span>
                )}

                {/* Status Messages as Header Badges */}
                {/* Closed RFQ Alert */}
                {(isExpired || rfq.procurement_status === 2) && submissions.length === 0 && (
                  <div className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-md">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Closed ({submissions.length} submissions)
                  </div>
                )}

                {/* OTP Confirmation Alert */}
                {rfq.procurement_status === 2 && (
                  <div className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 8A6 6 0 006 8v2.293l-2.293 2.293A1 1 0 004 14h12a1 1 0 00.707-1.707L14 10.293V8zM7.757 13.757a6 6 0 006.486 0" clipRule="evenodd" />
                    </svg>
                    Awaiting OTP
                    <button
                      onClick={handleShowOTPModal}
                      className="ml-1 underline hover:no-underline"
                      title="Enter OTP Codes"
                    >
                      Enter
                    </button>
                  </div>
                )}

                {/* Single Supplier Alert */}
                {!isExpired && rfq.procurement_status === 1 && getSuppliers(rfq).length === 1 && submissions.length === 1 && (
                  <div className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-md">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Single Supplier
                  </div>
                )}

                {/* All Suppliers Submitted Alert */}
                {(() => {
                  const suppliers = getSuppliers(rfq);
                  return !isExpired && rfq.procurement_status === 1 && suppliers.length > 1 && suppliers.length === submissions.length && (
                    <div className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-md">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      All {suppliers.length} Submitted
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RFQ Information Bar */}
      <div className="container mx-auto px-4 mb-4">
        <div className="bg-white border shadow-sm rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs">
                <span className="font-medium text-gray-500">Ref:</span>
                <span className="font-bold ml-1 text-gray-900">{rfq.procurement_ref}</span>
              </div>
              <div className="text-xs">
                <span className="font-medium text-gray-500">Created:</span>
                <span className="font-bold ml-1 text-gray-900">
                  {rfq.procurement_date || (rfq.procurement_time ? new Date(rfq.procurement_time * 1000).toLocaleDateString('en-GB') : 'Not specified')}
                </span>
              </div>
              {rfq.user_full_name && rfq.user_full_name !== 'Unknown' && (
                <div className="text-xs">
                  <span className="font-medium text-gray-500">By:</span>
                  <span className="font-bold ml-1 text-gray-900">
                    {rfq.user_full_name}
                  </span>
                </div>
              )}
              {rfq.department_name && rfq.department_name !== 'Unknown' && (
                <div className="text-xs">
                  <span className="font-medium text-gray-500">Dept:</span>
                  <span className="font-bold ml-1 text-gray-900">
                    {rfq.department_name}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs">
                <span className="font-medium text-gray-500">Closing:</span>
                <span className={`font-bold ml-1 ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                  {remainingTime}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary, Specification Items, and RFQ Description Row */}
      <div className="container mx-auto px-4 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Stacked Summary and Specification Items */}
          <div className="space-y-4">
            {/* Summary Card */}
            <div className="bg-white border shadow-sm rounded-lg">
              <div className="rounded-t-lg px-4 py-3 bg-gradient-primary text-white">
                <h6 className="mb-0 font-bold text-xs text-white flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Summary
                </h6>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center p-2 rounded-lg border bg-blue-50 border-blue-200 flex-1">
                    <span className="text-lg font-bold text-blue-600">{submissions.length}</span>
                    <span className="text-xs font-medium text-gray-600">Submissions</span>
                  </div>
 
                  <div className="flex flex-col items-center p-2 rounded-lg border bg-blue-50 border-blue-200 flex-1">
                    <span className="text-lg font-bold text-blue-600">
                      {getSuppliers(rfq).length}
                    </span>
                    <span className="text-xs font-medium text-gray-600">Suppliers</span>
                  </div>
                  <div className="flex flex-col items-center p-2 rounded-lg border bg-gray-50 border-gray-200 flex-1">
                    <span className="text-lg font-bold text-gray-600">
                      {rfq.accessed ? rfq.accessed.length : 0}
                    </span>
                    <span className="text-xs font-medium text-gray-600">Accessed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Specification Items Card */}
            <div className="bg-white border shadow-sm rounded-lg">
              <div className="rounded-t-lg px-4 py-3 bg-gradient-info text-white">
                <h6 className="mb-0 font-bold text-xs text-white">
                  <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Specification Items
                </h6>
              </div>
              <div className="p-4">
                <div className="overflow-x-auto max-h-48">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        try {
                          const items = rfq.specification_items 
                            ? parsePhpSerialized(rfq.specification_items, [])
                            : parsePhpSerialized(rfq.procurement_items, []);
                          
                          const quantities = rfq.specification_qty 
                            ? parsePhpSerialized(rfq.specification_qty, [])
                            : parsePhpSerialized(rfq.procurement_qty, []);
                          
                          if (items.length === 0) {
                            return (
                              <tr>
                                <td colSpan="2" className="px-2 py-1 text-xs text-center text-gray-500">No items specified</td>
                              </tr>
                            );
                          }
                          
                          return items.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-2 py-1 text-xs text-gray-900">{item}</td>
                              <td className="px-2 py-1 text-xs text-center text-gray-900">{quantities[index] || 0}</td>
                            </tr>
                          ));
                        } catch (e) {
                          console.error('Error parsing specification data:', e);
                          return (
                            <tr>
                              <td colSpan="2" className="px-2 py-1 text-xs text-center text-gray-500">Error loading specification data</td>
                            </tr>
                          );
                        }
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - RFQ Description */}
          {rfq.procurement_text ? (
            <div className="bg-white border shadow-sm rounded-lg lg:col-span-2">
              <div className="rounded-t-lg px-4 py-3 bg-gradient-warning text-white">
                <h6 className="mb-0 font-bold text-xs text-white">
                  RFQ Description
                </h6>
              </div>
              <div className="p-4">
                <div 
                  className="text-xs text-gray-900" 
                  dangerouslySetInnerHTML={{ __html: rfq.procurement_text }}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white border shadow-sm rounded-lg lg:col-span-2">
              <div className="rounded-t-lg px-4 py-3 bg-gradient-warning text-white">
                <h6 className="mb-0 font-bold text-xs text-white">
                  RFQ Description
                </h6>
              </div>
              <div className="p-4">
                <div className="text-center py-4">
                  <svg className="mx-auto h-6 w-6 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-1 text-xs font-medium text-gray-900">No description provided</h3>
                  <p className="mt-1 text-xs text-gray-500">This RFQ has no additional description.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Width Submissions Section - Only show when unlocked */}
      {isSubmissionsUnlocked(rfq) && (
        <div className="container mx-auto px-4 mb-4">
          <div className="bg-white border shadow-sm rounded-lg">
            <div className="rounded-t-lg px-4 py-3 bg-gradient-success text-white">
              <h6 className="mb-0 font-bold text-xs text-white">
                <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Submissions ({submissions.length})
                {rfq.procurement_status === 3 && (
                  <span className="ml-2 px-2 py-0.5 text-xs text-white bg-blue-500 rounded-full">
                    Manually Unlocked
                  </span>
                )}
                {isRFQExpired(rfq) && rfq.procurement_status !== 3 && getSuppliers(rfq).length > 1 && (
                  <span className="ml-2 px-2 py-0.5 text-xs text-white bg-orange-500 rounded-full">
                    Auto-Unlocked (Expired)
                  </span>
                )}
                {getSuppliers(rfq).length === 1 && rfq.procurement_status !== 3 && !isRFQExpired(rfq) && (
                  <span className="ml-2 px-2 py-0.5 text-xs text-white bg-green-500 rounded-full">
                    Single Supplier
                  </span>
                )}
              </h6>
            </div>
            <div className="p-4">
              {submissions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider">Company Name</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider">Contact Number</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider">Email Address</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider">Submitted</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-white uppercase tracking-wider">Quotation</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {submissions.map((submission, index) => (
                        <tr key={submission.quotation_id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900">
                            {submission.quotation_company_name || 'Unknown'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">
                            {submission.quotation_tel || 'N/A'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">
                            {submission.quotation_email_address || 'N/A'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">
                            {submission.quotation_time ? new Date(submission.quotation_time * 1000).toLocaleDateString('en-GB') + ' ' + new Date(submission.quotation_time * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Unknown'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap text-center text-xs">
                            <button
                              onClick={() => handleViewQuote(submission)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                              View Quote
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <svg className="mx-auto h-6 w-6 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-1 text-xs font-medium text-gray-900">No submissions yet</h3>
                  <p className="mt-1 text-xs text-gray-500">Suppliers haven't submitted any quotes yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show locked message when submissions are not unlocked but there are submissions */}
      {!isSubmissionsUnlocked(rfq) && submissions.length > 0 && (
        <div className="container mx-auto px-4 mb-4">
          <div className="bg-white border shadow-sm rounded-lg">
            <div className="rounded-t-lg px-4 py-3 card-header-white bg-gradient-warning">
              <h6 className="mb-0 font-bold text-xs text-white">
                <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Submissions Locked ({submissions.length})
              </h6>
            </div>
            <div className="compact-body p-4">
              <div className="text-center">
                <svg className="mx-auto h-8 w-8 mb-3" style={{ color: 'var(--text-secondary)' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {submissions.length} submission{submissions.length !== 1 ? 's' : ''} received
                </h3>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Quotations from multiple suppliers will be viewable after the closing date ({rfq.procurement_closing} at {rfq.procurement_closing_time}) or when manually unlocked.
                  {getSuppliers(rfq).length === 1 && (
                    <span className="block mt-1 font-medium text-green-600">
                      Note: Single supplier RFQs allow immediate viewing of submissions.
                    </span>
                  )}
                </p>
                {shouldShowUnlockButton(rfq) && (
                  <button
                    onClick={handleUnlock}
                    className="mt-3 inline-flex items-center px-3 py-2 text-xs font-medium text-white rounded-md shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: 'var(--color-success)' }}
                  >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Unlock Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}      {/* Full Width Sent Section */}
      <div className="container mx-auto px-4 mb-4">
        <div className="compact-card border shadow-sm rounded-lg" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-light)' }}>
          <div className="rounded-t-lg px-4 py-3 card-header-white bg-gradient-primary">
            <h6 className="mb-0 font-bold text-xs text-white">
              <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Suppliers ({getSuppliers(rfq).length})
            </h6>
          </div>
          <div className="compact-body p-2">
            {(() => {
              const suppliers = getSuppliers(rfq);
              return suppliers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-medium)' }}>
                    <thead style={{ backgroundColor: 'var(--picton-blue)', color: 'white' }}>
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider w-1/4">Company Name</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider w-1/4">Email Address</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider w-1/6">Actions</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider w-1/6">Sent</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-white uppercase tracking-wider w-1/12">Count</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-white uppercase tracking-wider w-1/12">Accessed</th>
                      </tr>
                    </thead>
                    <tbody style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-light)' }} className="divide-y">
                      {suppliers.map((sentItem, index) => (
                      <tr key={sentItem.rfq_id || index} style={{ backgroundColor: index % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-primary)' }}>
                        <td className="px-2 py-1 text-xs font-medium w-1/4" style={{ color: 'var(--text-primary)' }}>
                          <div className="truncate" title={sentItem.BP_Name || 'Unknown'}>
                            {sentItem.BP_Name || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-xs w-1/4" style={{ color: 'var(--text-primary)' }}>
                          <div 
                            className="truncate cursor-help" 
                            title={sentItem.E_Mail || 'N/A'}
                            style={{ maxWidth: '150px' }}
                          >
                            {(() => {
                              const emails = sentItem.E_Mail || 'N/A';
                              if (emails === 'N/A') return emails;
                              
                              // Split by comma and show only the first email with ellipsis if there are more
                              const emailList = emails.split(',').map(email => email.trim());
                              if (emailList.length > 1) {
                                return `${emailList[0]}... (+${emailList.length - 1})`;
                              }
                              return emailList[0];
                            })()}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-xs w-1/6">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleOpenSupplierLink(sentItem)}
                              className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded transition-colors"
                              style={{ 
                                color: 'white', 
                                backgroundColor: 'var(--color-info)', 
                              }}
                              title="Open supplier portal link"
                            >
                              Link
                            </button>
                            <button
                              onClick={() => handleResend(sentItem)}
                              className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded transition-colors"
                              style={{ 
                                color: 'white', 
                                backgroundColor: 'var(--color-success)', 
                              }}
                              title="Resend RFQ to supplier"
                            >
                              Resend
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-1 text-xs w-1/6" style={{ color: 'var(--text-primary)' }}>
                          <div className="truncate" title={sentItem.rfq_sent ? new Date(sentItem.rfq_sent * 1000).toLocaleDateString('en-GB') + ' ' + new Date(sentItem.rfq_sent * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Unknown'}>
                            {sentItem.rfq_sent ? new Date(sentItem.rfq_sent * 1000).toLocaleDateString('en-GB') + ' ' + new Date(sentItem.rfq_sent * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Unknown'}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-center text-xs w-1/12" style={{ color: 'var(--text-primary)' }}>
                          {sentItem.send_count || 0}
                        </td>
                        <td className="px-2 py-1 text-center text-xs w-1/12">
                          {rfq.accessed && rfq.accessed.includes(sentItem.BP_Code) ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              No
                            </span>
                          )}
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
              <div className="text-center py-4">
                <svg className="mx-auto h-6 w-6 mb-2" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-1 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>No RFQs sent yet</h3>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>RFQ has not been sent to any suppliers yet.</p>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      {selectedQuote && (
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          quote={{
            ...selectedQuote,
            // Parse the serialized quote data
            quotation_comments: selectedQuote.quotation_comments 
              ? parsePhpSerialized(selectedQuote.quotation_comments, [])
              : [],
            quotation_amounts: selectedQuote.quotation_amounts 
              ? parsePhpSerialized(selectedQuote.quotation_amounts, [])
              : [],
            quotation_units: selectedQuote.quotation_units 
              ? parsePhpSerialized(selectedQuote.quotation_units, [])
              : [],
            quotation_vats: selectedQuote.quotation_vats 
              ? parsePhpSerialized(selectedQuote.quotation_vats, [])
              : []
          }}
          specification={{
            ...rfq,
            // Parse the serialized items for the modal
            specification_items: rfq.specification_items 
              ? parsePhpSerialized(rfq.specification_items, [])
              : parsePhpSerialized(rfq.procurement_items, []),
            specification_qty: rfq.specification_qty 
              ? parsePhpSerialized(rfq.specification_qty, [])
              : parsePhpSerialized(rfq.procurement_qty, [])
          }}
          supplier={getSuppliers(rfq).find(s => s.BP_Code === selectedQuote.quotation_company_code || s.BP_Code === selectedQuote.supplier_code)}
          onEvaluate={() => {}} // RFQs don't have evaluation functionality
          isEvaluating={false}
          canEvaluate={false}
        />
      )}
      
      {/* Debug Modal */}
      {showDebugModal && rfq && (
        <DebugModal 
          show={showDebugModal}
          onHide={() => setShowDebugModal(false)}
          data={rfq}
        />
      )}

      {/* OTP Verification Modal */}
      {showOTPModal && (
        <Modal show={showOTPModal} onHide={() => setShowOTPModal(false)} size="lg">
          <Modal.Header closeButton style={{ background: colors.steelBlue, color: colors.textWhite }}>
            <Modal.Title>
              <i className="fas fa-key me-2"></i>
              Enter OTP Codes - {rfq.procurement_ref}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  OTP codes have been sent to the approvers below. Please enter the codes to unlock the RFQ for quotation viewing.
                </p>
              </div>
              
              {approvers.length > 0 ? (
                <div className="space-y-3">
                  {approvers.map((approver, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{approver.user_full_name}</div>
                        <div className="text-xs text-gray-500">{approver.user_email || 'No email'}</div>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Enter OTP code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={otpValues[index] || ''}
                          onChange={(e) => handleOTPChange(index, e.target.value)}
                          maxLength={6}
                        />
                      </div>
                      <div className="flex-none">
                        <button
                          className="px-3 py-1 text-xs text-white rounded hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: 'var(--color-info)' }}
                          onClick={() => handleResendOTP(approver, index)}
                        >
                          Resend
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No approvers found</h3>
                  <p className="mt-1 text-sm text-gray-500">Unable to load the approvers list.</p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex justify-between w-full">
              <button
                onClick={() => setShowOTPModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleOTPSubmit}
                className="px-4 py-2 text-sm text-white rounded-md hover:opacity-90 transition-opacity"
                style={{ backgroundColor: 'var(--color-success)' }}
                disabled={Object.keys(otpValues).length === 0}
              >
                Confirm Unlock
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {/* Extend Closing Date Modal */}
      {showExtendModal && (
        <Modal 
          show={showExtendModal} 
          onHide={() => setShowExtendModal(false)} 
          size="md" 
          centered
          style={{ overflow: 'visible' }}
          dialogClassName="modal-extend-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl border-0" style={{ overflow: 'visible' }}>
            {/* Header */}
            <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3">
              <button
                onClick={() => setShowExtendModal(false)}
                className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-center text-white">
                <div className="bg-white/20 rounded-full p-1.5 mr-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Extend Closing Date</h2>
                  <p className="text-white/90 text-xs">{rfq.procurement_ref}</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4" style={{ minHeight: '400px', overflow: 'visible' }}>
              {/* Current Info */}
              <div className="bg-blue-50 rounded-md p-3 border border-blue-200">
                <div className="flex items-center text-blue-800">
                  <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium text-sm">Current: {rfq?.procurement_closing} at {rfq?.procurement_closing_time}</span>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2" style={{ position: 'relative', overflow: 'visible' }}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    New Closing Date
                  </label>
                  <div style={{ position: 'relative', zIndex: 100 }}>
                    <CustomDatePicker
                      value={extendData.newDate}
                      onChange={(date) => setExtendData(prev => ({ ...prev, newDate: date }))}
                      placeholder="Select date"
                      className="w-full text-sm"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    value={extendData.newTime}
                    onChange={(e) => setExtendData(prev => ({ ...prev, newTime: e.target.value }))}
                    required
                  />
                </div>
              </div>

              {/* Notification Toggle */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Supplier Notification
                </label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors appearance-none bg-white"
                  value={extendData.notifySuppliers ? '1' : '0'}
                  onChange={(e) => setExtendData(prev => ({ ...prev, notifySuppliers: e.target.value === '1' }))}
                >
                  <option value="1">Yes - Send notification</option>
                  <option value="0">No - Extend silently</option>
                </select>
              </div>

              {/* Preview */}
              {extendData.newDate && extendData.newTime && (
                <div className="bg-green-50 rounded-md p-3 border border-green-200">
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-green-700">New closing:</span>
                      <span className="font-medium text-green-900">
                        {extendData.newDate} at {extendData.newTime}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-green-700">Notification:</span>
                      <span className="text-green-900">
                        {extendData.notifySuppliers ? 'Suppliers will be notified' : 'Silent extension'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 flex justify-end gap-2 border-t border-gray-200">
              <button
                onClick={() => setShowExtendModal(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExtendSubmit}
                disabled={!extendData.newDate || !extendData.newTime}
                className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-md hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Extend
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Suppliers Modal */}
      {showEditSuppliersModal && (
        <Modal show={showEditSuppliersModal} onHide={() => setShowEditSuppliersModal(false)} size="xl">
          <Modal.Header closeButton style={{ background: colors.steelBlue, color: colors.textWhite }}>
            <Modal.Title>
              <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit Suppliers - {rfq?.procurement_ref}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Select the suppliers who should receive this RFQ. Changes will update the specification's supplier list.
                </p>
              </div>

              {/* Dual Pane Supplier Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Available Suppliers */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Available Suppliers</h4>
                    <input
                      type="text"
                      placeholder="Search suppliers..."
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Click on a supplier to add it to the selected list</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto p-2">
                    {allSuppliers
                      .filter(supplier => {
                        // Check if supplier is already selected
                        const isSelected = selectedSuppliers.some(s => {
                          const supplierCode = typeof s === 'object' ? s.BP_Code : s;
                          return supplierCode === supplier.BP_Code;
                        });
                        
                        // Filter by search term
                        const matchesSearch = supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase());
                        
                        return !isSelected && matchesSearch;
                      })
                      .map((supplier) => (
                        <div
                          key={supplier.BP_Code}
                          onClick={() => {
                            setSelectedSuppliers(prev => [...prev, {
                              BP_Code: supplier.BP_Code,
                              BP_Name: supplier.BP_Name
                            }]);
                            setSupplierNames(prev => ({
                              ...prev,
                              [supplier.BP_Code]: supplier.BP_Name
                            }));
                          }}
                          className="flex items-center justify-between p-2 rounded-md hover:bg-green-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {supplier.BP_Name}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="ml-2 text-green-600 hover:text-green-800 flex-shrink-0"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    {allSuppliers.filter(supplier => {
                      const isSelected = selectedSuppliers.some(s => {
                        const supplierCode = typeof s === 'object' ? s.BP_Code : s;
                        return supplierCode === supplier.BP_Code;
                      });
                      const matchesSearch = supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase());
                      return !isSelected && matchesSearch;
                    }).length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-sm">No suppliers found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Suppliers */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="bg-green-50 px-4 py-3 border-b border-gray-300">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        Selected Suppliers ({selectedSuppliers.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        {selectedSuppliers.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedSuppliers([])}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto p-2">
                    {selectedSuppliers.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-sm">No suppliers selected</p>
                        <p className="text-xs text-gray-400 mt-1">Click suppliers from the left to add them</p>
                      </div>
                    ) : (
                      <>
                        {selectedSuppliers.map((supplier) => {
                          const supplierCode = typeof supplier === 'object' ? supplier.BP_Code : supplier;
                          const supplierName = typeof supplier === 'object' && supplier.BP_Name 
                            ? supplier.BP_Name 
                            : (supplierNames[supplierCode] || `Supplier ${supplierCode}`);
                          
                          return (
                            <div 
                              key={supplierCode}
                              className="flex items-center justify-between p-2 bg-white border-b border-gray-100 last:border-b-0"
                            >
                              <span className="text-sm font-medium">{supplierName}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedSuppliers(prev => prev.filter(s => {
                                    const code = typeof s === 'object' ? s.BP_Code : s;
                                    return code !== supplierCode;
                                  }));
                                }}
                                className="ml-2 text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              {allSuppliers.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const allSuppliersData = allSuppliers.map(s => ({
                        BP_Code: s.BP_Code,
                        BP_Name: s.BP_Name
                      }));
                      const newNames = {};
                      allSuppliers.forEach(s => {
                        newNames[s.BP_Code] = s.BP_Name;
                      });
                      setSupplierNames(prev => ({...prev, ...newNames}));
                      setSelectedSuppliers(allSuppliersData);
                    }}
                    className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSuppliers([])}
                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSupplierSearch('')}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: colors.antiflashWhite }}>
            <div className="flex justify-between items-center w-full">
              <span className="text-sm text-gray-600">
                {selectedSuppliers.length} supplier{selectedSuppliers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowEditSuppliersModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isUpdatingSuppliers}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateSuppliers}
                  disabled={isUpdatingSuppliers || selectedSuppliers.length === 0}
                  className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: colors.steelBlue }}
                >
                  {isUpdatingSuppliers ? (
                    <>
                      <svg className="animate-spin w-4 h-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update Suppliers'
                  )}
                </button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      </div>
    </>
  );
}

// Enhanced Debug Modal Component with organized data structure
const DebugModal = ({ show, onHide, data }) => {
  const { user: authUser } = useAuth();
  const [selectedSection, setSelectedSection] = useState('overview');
  
  if (!data) return null;

  const dataStructure = {
    overview: {
      title: 'Overview',
      icon: '📋',
      data: {
        procurement_id: data.procurement_id,
        procurement_ref: data.procurement_ref,
        procurement_status: data.procurement_status,
        procurement_title: data.procurement_title,
        specification_title: data.specification_title,
        department_name: data.department_name,
        user_full_name: data.user_full_name,
        procurement_closing: data.procurement_closing,
        procurement_time: data.procurement_time
      }
    },
    specification: {
      title: 'Specification Data',
      icon: '📦',
      data: {
        specification_id: data.specification_id,
        specification_items: data.specification_items,
        specification_qty: data.specification_qty,
        specification_supplier: data.specification_supplier,
        procurement_items: data.procurement_items,
        procurement_qty: data.procurement_qty,
        parsed_items: data.specification_items 
          ? parsePhpSerialized(data.specification_items, [])
          : parsePhpSerialized(data.procurement_items, []),
        parsed_quantities: data.specification_qty 
          ? parsePhpSerialized(data.specification_qty, [])
          : parsePhpSerialized(data.procurement_qty, []),
        parsed_suppliers: parsePhpSerialized(data.specification_supplier, [])
      }
    },
    submissions: {
      title: 'Submissions & Quotes',
      icon: '💰',
      data: {
        submissions: data.submissions || [],
        quotes: data.quotes || [],
        suppliers: data.suppliers || [],
        sent: data.sent || [],
        accessed: data.accessed || [],
        files: data.files || []
      }
    },
    raw: {
      title: 'Raw Data',
      icon: '🔧',
      data: data
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="debug-modal">
      <Modal.Header closeButton style={{ background: colors.steelBlue, color: colors.textWhite }}>
        <Modal.Title>
          <i className="fas fa-bug me-2"></i>
          Debug Information - {data.procurement_ref}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="flex" style={{ height: '600px' }}>
          {/* Navigation Sidebar */}
          <div className="w-1/4 border-r" style={{ backgroundColor: colors.antiflashWhite, borderColor: colors.ashGray }}>
            <div className="p-4">
              <h6 className="text-sm font-bold mb-4 uppercase tracking-wider" style={{ color: colors.ashGray }}>
                Data Sections
              </h6>
              <div className="space-y-1">
                {Object.entries(dataStructure).map(([key, section]) => (
                  <button
                    key={key}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                      selectedSection === key 
                        ? 'shadow-sm' 
                        : 'hover:shadow-sm'
                    }`}
                    style={{
                      backgroundColor: selectedSection === key ? colors.steelBlue : 'transparent',
                      color: selectedSection === key ? colors.textWhite : colors.textPrimary,
                      border: selectedSection === key ? 'none' : `1px solid ${colors.ashGray}33`
                    }}
                    onClick={() => setSelectedSection(key)}
                  >
                    <span className="mr-2">{section.icon}</span>
                    {section.title}
                    {key === 'overview' && (
                      <div className="text-xs mt-1 opacity-75">
                        ID: {data.procurement_id}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Content Area */}
          <div className="flex-1">
            <div className="p-4 h-full overflow-auto">
              <div className="flex items-center mb-4">
                <span className="mr-3 text-xl">{dataStructure[selectedSection].icon}</span>
                <h5 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                  {dataStructure[selectedSection].title}
                </h5>
              </div>
              
              {selectedSection === 'raw' ? (
                <div className="w-full">
                  <pre 
                    className="p-4 rounded-lg text-xs overflow-auto whitespace-pre-wrap break-words" 
                    style={{ 
                      backgroundColor: colors.textPrimary,
                      color: colors.antiflashWhite,
                      maxHeight: '500px',
                      maxWidth: '100%',
                      lineHeight: '1.4',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {JSON.stringify(dataStructure[selectedSection].data, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="structured-data">
                  {selectedSection === 'overview' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="rounded-lg border shadow-sm" style={{ borderColor: colors.steelBlue }}>
                        <div className="px-4 py-2 rounded-t-lg" style={{ backgroundColor: colors.steelBlue }}>
                          <h6 className="text-sm font-semibold text-white">RFQ Details</h6>
                        </div>
                        <div className="p-4" style={{ backgroundColor: colors.light }}>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium">ID:</span>
                              <span>{data.procurement_id}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Reference:</span>
                              <span>{data.procurement_ref}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Status:</span>
                              <span>{data.procurement_status}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Closing:</span>
                              <span>{data.procurement_closing}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg border shadow-sm" style={{ borderColor: colors.cambridgeBlue }}>
                        <div className="px-4 py-2 rounded-t-lg" style={{ backgroundColor: colors.cambridgeBlue }}>
                          <h6 className="text-sm font-semibold text-white">Project Info</h6>
                        </div>
                        <div className="p-4" style={{ backgroundColor: colors.light }}>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium">Title:</span>
                              <span className="text-right ml-2">{data.procurement_title}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Department:</span>
                              <span className="text-right ml-2">{data.department_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Requester:</span>
                              <span>{data.user_full_name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="w-full" style={{ backgroundColor: colors.antiflashWhite }}>
                    <pre 
                      className="p-4 rounded-lg text-xs overflow-auto whitespace-pre-wrap break-words" 
                      style={{ 
                        maxHeight: '400px',
                        maxWidth: '100%',
                        lineHeight: '1.4',
                        margin: 0,
                        color: colors.textPrimary,
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {JSON.stringify(dataStructure[selectedSection].data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: colors.antiflashWhite, borderTop: `1px solid ${colors.ashGray}33` }}>
        <div className="flex justify-between items-center w-full">
          <small style={{ color: colors.ashGray }}>
            Current Section: <strong>{dataStructure[selectedSection].title}</strong>
          </small>
          <button
            onClick={onHide}
            className="px-4 py-2 bg-secondary text-white rounded transition-colors"
            style={{ backgroundColor: colors.steelBlue }}
          >
            Close Debug
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RFQView;
